import { useNavigate, useSearchParams } from 'react-router-dom';
import { useLoading } from 'contexts/loading';
import { useCallback, useEffect, useMemo, useState } from 'react';
import { Dashboard, GetFilesParams, TFile, User } from 'types/user';
import { SIZE_PAGE_CONTROL_PANEL } from 'const/paging';
import { useAsyncCallback } from 'hooks/useAsyncCallback';
import {
  GridFilterModel,
  GridPaginationModel,
  GridRowParams,
  GridSortModel
} from '@mui/x-data-grid';
import { getCountFileByStatus, getFiles } from 'services/files';
import { useDebounce } from 'use-debounce';
import { adminGetAllUser } from 'services/user';
import { ClientOption } from './components/FileForm/hook';
import { getClientOptionAll } from 'services/client';
export const TAB = {
  OPEN_FILES: 'open-files',
  ON_HOLD: 'on-hold',
  REDEMPTION: 'redemption'
};

export const headerTab = [
  { label: 'Open Files', param: TAB.OPEN_FILES, value: 0 },
  { label: 'On Hold', param: TAB.ON_HOLD, value: 1 },
  { label: 'Redemption', param: TAB.REDEMPTION, value: 2 }
];

type FileState = {
  files: TFile[];
  total: number;
};

type QueryParams = {
  page: number;
  sortBy?: string;
  order?: 'asc' | 'desc' | null;
  status?: string[];
  client?: string[];
  agent?: string[];
};

export const useInjection = () => {
  const navigate = useNavigate();
  const { loading } = useLoading();
  const [searchParams, setSearchParams] = useSearchParams();
  const [dashboard, setDashboard] = useState<Dashboard>();

  const [fileStates, setFileStates] = useState<{ [key: string]: FileState }>({
    [TAB.OPEN_FILES]: { files: [], total: 0 },
    [TAB.ON_HOLD]: { files: [], total: 0 },
    [TAB.REDEMPTION]: { files: [], total: 0 }
  });

  const [queryParams, setQueryParams] = useState<Record<string, QueryParams>>({
    [TAB.OPEN_FILES]: { page: 1 },
    [TAB.ON_HOLD]: { page: 1 },
    [TAB.REDEMPTION]: { page: 1 }
  });

  const [searchText, setSearchText] = useState<string>('');
  const [debouncedSearchText] = useDebounce(searchText, 800);

  const handleSortModelChange = useCallback((model: GridSortModel, tab: string) => {
    const sort = model[0];
    setQueryParams((prev) => ({
      ...prev,
      [tab]: {
        ...prev[tab],
        page: 1,
        sortBy: sort?.field,
        order: sort?.sort ?? null
      }
    }));
  }, []);
  const handleFilterModelChange = useCallback((model: GridFilterModel, tab: string) => {
    const statusValues = model.items
      .filter((i) => i.field === 'status' && i.value)
      .flatMap((i) => (Array.isArray(i.value) ? i.value : [i.value]));

    const clientValues = model.items
      .filter((i) => i.field === 'clientOption' && i.value)
      .flatMap((i) => (Array.isArray(i.value) ? i.value : [i.value]));

    const agentValues = model.items
      .filter((i) => i.field === 'dispatchFiles' && i.value)
      .flatMap((i) => (Array.isArray(i.value) ? i.value : [i.value]));

    setQueryParams((prev) => ({
      ...prev,
      [tab]: {
        ...prev[tab],
        page: 1,
        status: statusValues,
        client: clientValues,
        agent: agentValues
      }
    }));
  }, []);

  const fetchDataForTab = useCallback(
    async ({
      tab,
      qp,
      searchText
    }: {
      tab: string;
      qp: Partial<GetFilesParams>;
      searchText: string;
    }) => {
      const data = await getFiles({
        page: qp.page,
        size: SIZE_PAGE_CONTROL_PANEL,
        fileStatus: tab,
        searchText: searchText || undefined,
        sortBy: qp?.sortBy || undefined,
        order: qp?.order || undefined,
        status: qp?.status || undefined,
        client: qp?.client || undefined,
        agent: qp?.agent || undefined
      });
      setFileStates((prev) => ({
        ...prev,
        [tab]: { files: data.files, total: data.total }
      }));
    },
    []
  );
  const { asyncCallback: asyncFetchUser } = useAsyncCallback(fetchDataForTab, []);
  useEffect(() => {
    asyncFetchUser({
      tab: TAB.OPEN_FILES,
      qp: queryParams[TAB.OPEN_FILES],
      searchText: debouncedSearchText
    });
  }, [queryParams[TAB.OPEN_FILES], asyncFetchUser, debouncedSearchText]);
  useEffect(() => {
    asyncFetchUser({
      tab: TAB.ON_HOLD,
      qp: queryParams[TAB.ON_HOLD],
      searchText: debouncedSearchText
    });
  }, [queryParams[TAB.ON_HOLD], asyncFetchUser, debouncedSearchText]);
  useEffect(() => {
    asyncFetchUser({
      tab: TAB.REDEMPTION,
      qp: queryParams[TAB.REDEMPTION],
      searchText: debouncedSearchText
    });
  }, [queryParams[TAB.REDEMPTION], asyncFetchUser, debouncedSearchText]);

  const fetchDataDashboard = useCallback(async () => {
    const dashboardData = await getCountFileByStatus();
    setDashboard(dashboardData.dashboard);
  }, []);

  useEffect(() => {
    fetchDataDashboard();
  }, []);

  const handlePaginationModelChange = useCallback(
    (newPaginationModel: GridPaginationModel, tab: string) => {
      const newPage = newPaginationModel.page + 1;
      setQueryParams((prev) => ({
        ...prev,
        [tab]: {
          ...prev[tab],
          page: newPage
        }
      }));
    },
    []
  );

  const handleSearch = useCallback((valueSearch: string) => {
    setSearchText(valueSearch);
  }, []);

  useEffect(() => {
    setQueryParams((prev) => ({
      [TAB.OPEN_FILES]: { ...prev[TAB.OPEN_FILES], page: 1 },
      [TAB.ON_HOLD]: { ...prev[TAB.ON_HOLD], page: 1 },
      [TAB.REDEMPTION]: { ...prev[TAB.REDEMPTION], page: 1 }
    }));
  }, []);
  const handleRowClick = (params: GridRowParams) => {
    navigate(`/admin/files/${params.id}`);
  };

  const handleChangeTab = useCallback(
    (newValue: number) => {
      const nameTab = headerTab.find((item) => item.value === newValue)?.param;
      setSearchParams({ tab: nameTab || TAB.OPEN_FILES });
    },
    [setSearchParams]
  );
  const currentTab = useMemo(() => {
    const params = searchParams.get('tab');
    return headerTab.find((item) => item.param === params)?.value || 0;
  }, [searchParams]);

  const optionHeader = useMemo(() => {
    return [
      {
        label: 'Open Files',
        value: dashboard?.openFile
      },
      {
        label: 'On Hold',
        value: dashboard?.onHold
      },
      {
        label: 'Redemption',
        value: dashboard?.redemption
      }
    ];
  }, [dashboard]);

  const showValueSearch = useMemo(() => {
    return searchText;
  }, [searchText]);

  const [optionsUser, setOptionsUser] = useState<{ label: string; value: string }[]>([]);
  const [optionClient, setOptionClient] = useState<ClientOption[]>([]);

  const getUserBailiff = useCallback(async () => {
    const [data, dataOption] = await Promise.all([
      adminGetAllUser({ isActive: true }),
      getClientOptionAll()
    ]);
    const dataUserConvert = (data.users as User[]).map((item) => ({
      label: `${item.firstName} ${item.lastName} ( ${item.email} )`,
      value: item.id
    }));
    setOptionClient(dataOption.clientOptions);
    setOptionsUser(dataUserConvert);
  }, []);
  const { asyncCallback: asyncFetchBailiff } = useAsyncCallback(getUserBailiff, []);

  useEffect(() => {
    asyncFetchBailiff();
  }, []);
  return {
    optionsUser,
    // table-related states & handlers
    queryParams,
    setQueryParams,
    handlePaginationModelChange,
    handleSortModelChange,
    handleFilterModelChange,
    handleSearch,
    searchText,
    showValueSearch,
    // non-table-related exports
    tab: currentTab,
    searchParams,
    fileStates,
    handleRowClick,
    loading,
    handleChangeTab,
    dashboard,
    optionHeader,
    optionClient
  };
};
